import {
  Code2,
  FileSpreadsheet,
  FileText,
  MousePointerClick,
  Type,
} from 'lucide-react';

export const WORKSPACE_VARIANTS = {
  pdf: {
    mode: 'pdf',
    intakeMode: 'document',
    theme: 'brd',
    label: 'BRD Flow',
    description: 'BRD documents to full test coverage.',
    icon: FileText,
    badgeClass: 'bg-blue-50 text-blue-800 border-blue-200',
    activeClass: 'border-blue-300 bg-blue-50 text-blue-950 ring-blue-100',
    iconClass: 'bg-blue-50 text-blue-700',
    activeIconClass: 'bg-blue-600 text-white',
    checkClass: 'text-blue-700',
    sidebarKicker: 'BRD',
    sidebarTitle: 'Build complete test suites from requirements',
    sidebarDescription: 'Unified flow for extraction, planning, case authoring, and automation delivery.',
    sourceMetricLabel: 'Document',
    unitMetricLabel: 'Sections',
    intakeDescription: 'Upload requirements and prepare source context',
    intakeChecklist: {
      title: 'Intake Checklist',
      items: [
        {
          title: 'Use current requirements',
          description: 'Upload the latest approved BRD to avoid outdated scenarios.',
        },
        {
          title: 'Prefer searchable text',
          description: 'Choose a searchable PDF or Word document for cleaner extraction.',
        },
        {
          title: 'Reuse prior runs',
          description: 'Open a previous BRD when iterating on planning and generation.',
        },
      ],
    },
  },
  requirements: {
    mode: 'requirements',
    intakeMode: 'text',
    theme: 'requirements',
    label: 'Text Requirements',
    description: 'Requirements and screenshots.',
    icon: Type,
    badgeClass: 'bg-violet-50 text-violet-800 border-violet-200',
    activeClass: 'border-violet-300 bg-violet-50 text-violet-950 ring-violet-100',
    iconClass: 'bg-violet-50 text-violet-700',
    activeIconClass: 'bg-violet-600 text-white',
    checkClass: 'text-violet-700',
    sidebarKicker: 'Requirements',
    sidebarTitle: 'Turn written requirements into complete tests',
    sidebarDescription: 'Paste a focused requirement, analyze its coverage, then continue through cases, POM, scripts, and execution.',
    sourceMetricLabel: 'Requirement Source',
    unitMetricLabel: 'Sections',
    processingLabel: 'Text requirements',
    intakeDescription: 'Describe a focused feature and prepare its source context',
    intakeChecklist: {
      title: 'Requirement Checklist',
      items: [
        {
          title: 'Keep the scope focused',
          description: 'Describe one feature, workflow, or module so generated coverage stays precise.',
        },
        {
          title: 'Include rules and outcomes',
          description: 'Add user actions, validations, business rules, and the expected result.',
        },
        {
          title: 'Attach useful visuals',
          description: 'Add screenshots only when they clarify states, controls, or intent.',
        },
      ],
    },
  },
  'api-testing': {
    mode: 'api-testing',
    intakeMode: 'api',
    theme: 'api',
    label: 'API Testing',
    description: 'Swagger and OpenAPI contracts.',
    icon: Code2,
    badgeClass: 'bg-sky-50 text-sky-800 border-sky-200',
    activeClass: 'border-sky-300 bg-sky-50 text-sky-950 ring-sky-100',
    iconClass: 'bg-sky-50 text-sky-700',
    activeIconClass: 'bg-sky-600 text-white',
    checkClass: 'text-sky-700',
    sidebarKicker: 'API Testing',
    sidebarTitle: 'Generate API test coverage from contracts',
    sidebarDescription: 'Upload Swagger or OpenAPI, review operation-level coverage, then generate Playwright API scripts.',
    sourceMetricLabel: 'API Contract',
    unitMetricLabel: 'Operations',
    processingLabel: 'Swagger / OpenAPI',
    intakeDescription: 'Upload a contract and prepare operation-level source context',
    intakeChecklist: {
      title: 'Contract Checklist',
      items: [
        {
          title: 'Use the current contract',
          description: 'Upload the latest Swagger or OpenAPI definition for accurate coverage.',
        },
        {
          title: 'Validate the specification',
          description: 'Prefer a valid JSON or YAML contract with complete operation schemas.',
        },
        {
          title: 'Review operation scope',
          description: 'Confirm the endpoints and operations that should move into generation.',
        },
      ],
    },
  },
  'excel-testcases': {
    mode: 'excel-testcases',
    intakeMode: 'excel',
    theme: 'excel',
    label: 'Excel Test Cases',
    description: 'Existing testcase workbooks.',
    icon: FileSpreadsheet,
    badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    activeClass: 'border-emerald-300 bg-emerald-50 text-emerald-950 ring-emerald-100',
    iconClass: 'bg-emerald-50 text-emerald-700',
    activeIconClass: 'bg-emerald-600 text-white',
    checkClass: 'text-emerald-700',
    sidebarKicker: 'Excel Test Cases',
    sidebarTitle: 'Import ready-made test cases from workbooks',
    sidebarDescription: 'Upload an XLSX testcase sheet, confirm flexible column mappings, then continue through review and web automation.',
    sourceMetricLabel: 'Workbook',
    unitMetricLabel: 'Cases',
    processingLabel: 'Excel testcase import',
    intakeDescription: 'Upload a testcase workbook and prepare mapped coverage',
    intakeChecklist: {
      title: 'Excel Checklist',
      items: [
        {
          title: 'Use XLSX format',
          description: 'Upload an .xlsx workbook with test case rows in any sheet.',
        },
        {
          title: 'Review detected columns',
          description: 'Confirm mappings for steps, expected results, and test data before importing.',
        },
        {
          title: 'Keep steps readable',
          description: 'Numbered or multiline steps import cleanly into the automation flow.',
        },
      ],
    },
  },
  'feature-capture': {
    mode: 'feature-capture',
    theme: 'capture',
    label: 'Test Feature',
    description: 'Browser flows to cases and scripts.',
    icon: MousePointerClick,
    badgeClass: 'bg-teal-50 text-teal-800 border-teal-200',
    activeClass: 'border-teal-300 bg-teal-50 text-teal-950 ring-teal-100',
    iconClass: 'bg-teal-50 text-teal-700',
    activeIconClass: 'bg-teal-600 text-white',
    checkClass: 'text-teal-700',
    sidebarKicker: 'Test Feature',
    sidebarTitle: 'Applications and feature flows',
    sidebarDescription: 'Start from an application, then manage the features captured under it.',
  },
};

export const PRODUCT_WORKSPACE_MODES = [
  'pdf',
  'requirements',
  'api-testing',
  'excel-testcases',
  'feature-capture',
];

export const getWorkspaceVariantForIntake = (intakeMode = 'document') => {
  if (intakeMode === 'text') return WORKSPACE_VARIANTS.requirements;
  if (intakeMode === 'api') return WORKSPACE_VARIANTS['api-testing'];
  if (intakeMode === 'excel') return WORKSPACE_VARIANTS['excel-testcases'];
  return WORKSPACE_VARIANTS.pdf;
};
